/**
 * Support workflow & policies — automation request bodies (see
 * types/support-automation.ts).
 */
import { z } from 'zod';
import { SUPPORT_AUTOMATION_TRIGGERS } from '../types/support-automation.js';
/** Up to 90 days, in minutes. */
const minutes = z.number().int().min(1).max(129_600);
const text = (max) => z.string().trim().min(1).max(max);
export const SupportAutomationActionSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('send_message'), text: text(4000) }),
    z
        .object({
        type: z.literal('send_email'),
        to: z.enum(['customer', 'assignee', 'address']),
        address: z.string().trim().email().max(254).nullable().optional(),
        subject: text(200),
        text: text(8000),
    })
        .refine((a) => a.to !== 'address' || !!a.address, {
        message: 'Give the address to send to',
        path: ['address'],
    }),
    z.object({ type: z.literal('wait'), minutes }),
    z
        .object({
        type: z.literal('wait_reply'),
        timeoutMinutes: minutes,
        onTimeout: z.enum(['continue', 'stop', 'move']),
        timeoutStageId: z.string().uuid().nullable().optional(),
    })
        .refine((a) => a.onTimeout !== 'move' || !!a.timeoutStageId, {
        message: 'Choose the stage to move to when nobody replies',
        path: ['timeoutStageId'],
    }),
    z.object({ type: z.literal('move_stage'), stageId: z.string().uuid() }),
    z.object({ type: z.literal('assign'), userId: z.string().uuid().nullable() }),
    z.object({ type: z.literal('set_priority'), priorityId: z.string().uuid() }),
    z.object({ type: z.literal('add_note'), text: text(4000) }),
    z.object({
        type: z.literal('notify_team'),
        userIds: z.array(z.string().uuid()).min(1).max(50),
        text: text(4000),
    }),
]);
const automationFields = {
    stageId: z.string().uuid(),
    name: z.string().trim().min(1).max(100),
    trigger: z.enum(SUPPORT_AUTOMATION_TRIGGERS),
    triggerMinutes: z.number().int().min(0).max(129_600).nullable().optional(),
    triggerStageId: z.string().uuid().nullable().optional(),
    actions: z.array(SupportAutomationActionSchema).min(1).max(20),
    enabled: z.boolean().optional(),
};
const needsTime = (d) => d.trigger !== 'time_in_stage' || (d.triggerMinutes ?? 0) >= 1;
export const SupportAutomationInputSchema = z
    .object(automationFields)
    .refine(needsTime, {
    message: 'Say how long the ticket must be in the stage',
    path: ['triggerMinutes'],
});
export const UpdateSupportAutomationSchema = z
    .object(automationFields)
    .partial()
    .refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' })
    .refine(needsTime, {
    message: 'Say how long the ticket must be in the stage',
    path: ['triggerMinutes'],
});
export const SupportAutomationDryRunSchema = z.object({ ticketId: z.string().uuid() });
export const SupportAutomationIdParamSchema = z.object({ id: z.string().uuid() });
export const SupportAutomationRunParamSchema = z.object({
    ticketId: z.string().uuid(),
    runId: z.string().uuid(),
});
//# sourceMappingURL=support-automation.schema.js.map